import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Box,
  MenuItem,
  Typography,
} from "@mui/material";
import useUpdate from "../../api/useUpdate";
import useFetchObjects from "../../api/useFetchObjects";
import { useTranslation } from "react-i18next";

const ProductEdit = ({ open, onClose, editData, token }) => {
  const { t } = useTranslation();
  const { handleUpdate, loading, isSuccess } = useUpdate(
    "shop/products",
    token
  );
  const { data: categoriesData, loading: catLoading } = useFetchObjects(
    ["category"],
    "shop/categories",
    token
  );

  const categories = Array.isArray(categoriesData) ? categoriesData : categoriesData?.results || [];

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedShop, setSelectedShop] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [brandName, setBrandName] = useState("");

  // Set basic fields when editData changes.
  useEffect(() => {
    if (editData) {
      setName(editData.name);
      setDescription(editData.description || "");
      setBrandName(editData.brand_name || "");
      // Determine category id from editData:
      // If editData.category is an object, use its id; otherwise use editData.category directly.
      const catId =
        editData.category && editData.category.id
          ? String(editData.category.id)
          : editData.category
          ? String(editData.category)
          : "";
      setSelectedCategory(catId);

      const shopId =
        editData.shop && editData.shop.id
          ? String(editData.shop.id)
          : editData.shop
          ? String(editData.shop)
          : "";
      setSelectedShop(shopId);
    }
  }, [editData]);

  const handleFileChange = (e) => {
    setImageFile(e.target.files[0]);
  };

  const handleSubmit = () => {
    const formData = new FormData();
    formData.append("name", name.trim());
    formData.append("description", description);
    formData.append("category", selectedCategory);
    formData.append("brand_name", brandName.trim());
    formData.append("shop", selectedShop);
    if (imageFile) {
      formData.append("image", imageFile);
    }
    handleUpdate(editData.id, formData);
  };

  useEffect(() => {
    if (isSuccess) {
      setName("");
      setDescription("");
      setSelectedCategory("");
      setImageFile(null);
      setBrandName("");
      setSelectedShop("");
      onClose();
    }
  }, [isSuccess, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("product.editProduct")}</DialogTitle>
      <DialogContent>
        <Box component="form" noValidate sx={{ mt: 1 }}>
          <TextField
            autoFocus
            margin="dense"
            label={t("product.productName")}
            type="text"
            fullWidth
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label={t("product.description")}
            type="text"
            fullWidth
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <TextField
            select
            margin="dense"
            label={t("product.selectCategory")}
            fullWidth
            required
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            {catLoading ? (
              <MenuItem value="">
                <CircularProgress size={20} />
              </MenuItem>
            ) : (
              categories.map((cat) => (
                <MenuItem key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </MenuItem>
              ))
            )}
          </TextField>

          <Box sx={{ mt: 2 }}>
            <Button variant="contained" component="label">
              {t("product.uploadImage")}
              <input type="file" hidden onChange={handleFileChange} />
            </Button>
            {imageFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {imageFile.name}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={loading}>
          {t("product.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={loading || !name.trim() || !selectedCategory}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t("product.submit")
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProductEdit;
